import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Icon,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  Select,
  Spinner,
  Switch,
  VStack,
} from '@chakra-ui/react'
import React, { useEffect, useState } from 'react'
import { FiInfo } from 'react-icons/fi'
import { API_URL } from '../api/config'
import { useConfig } from './ConfigContext'

type ConfigModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const ConfigHelp: React.FC<{ explanation: string }> = ({ explanation }) => (
  <Popover placement="right" closeOnBlur={true}>
    <PopoverTrigger>
      <IconButton
        variant="ghost"
        colorScheme="blue"
        aria-label="Mais informações"
        icon={<Icon as={FiInfo} boxSize={4} />}
        size="xs"
      />
    </PopoverTrigger>
    <PopoverContent maxW="250px">
      <PopoverArrow />
      <PopoverCloseButton />
      <PopoverBody fontSize="xs" textAlign="left">
        {explanation}
      </PopoverBody>
    </PopoverContent>
  </Popover>
);

const ConfigModal: React.FC<ConfigModalProps> = ({ isOpen, onClose }) => {
  const { config, setConfig } = useConfig();

  // Estados de configuração
  const [darkMode, setDarkMode] = useState(config.darkMode);
  const [language, setLanguage] = useState(config.language);
  const [showRoomData, setShowRoomData] = useState(config.showRoomData);
  const [showUserData, setShowUserData] = useState(config.showUserData);
  const [qrCodeSearchAction, setQrCodeSearchAction] = useState(config.qrCodeSearchAction);
  const [autoOpenQRCodeScanner, setAutoOpenQRCodeScanner] = useState(config.autoOpenQRCodeScanner);
  const [showConfirmation, setShowConfirmation] = useState(config.showConfirmation);
  const [returnToAccessAfterAction, setReturnToAccessAfterAction] = useState(config.returnToAccessAfterAction);
  const [allowMultipleEntries, setAllowMultipleEntries] = useState(config.allowMultipleEntries);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Ao abrir o modal, busca todas as configurações do servidor de uma vez (batch)
  useEffect(() => {
    if (isOpen) {
      const fetchConfigs = async () => {
        try {
          setLoading(true);
          const keys = [
            "darkMode",
            "language",
            "showRoomData",
            "showUserData",
            "qrCodeSearchAction",
            "autoOpenQRCodeScanner",
            "showConfirmation",
            "returnToAccessAfterAction"
          ];
          const res = await fetch(`${API_URL}/system-config/batch`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ keys }),
          });
          if (res.ok) {
            const data: { [key: string]: string } = await res.json();
            setDarkMode(data.darkMode === 'true');
            setLanguage(data.language);
            setShowRoomData(data.showRoomData === 'true');
            setShowUserData(data.showUserData === 'true');
            setQrCodeSearchAction(data.qrCodeSearchAction);
            setAutoOpenQRCodeScanner(data.autoOpenQRCodeScanner === 'true');
            setShowConfirmation(data.showConfirmation === 'true');
            setReturnToAccessAfterAction(data.returnToAccessAfterAction === 'true');
            setAllowMultipleEntries(data.allowMultipleEntries === 'true');
          } else {
            console.error("Erro ao buscar configurações em batch:", res.statusText);
          }
        } catch (error) {
          console.error("Erro ao carregar configurações:", error);
        } finally {
          setLoading(false);
        }
      };
      fetchConfigs();
    }
  }, [isOpen]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await fetch(`${API_URL}/system-config/darkMode`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: darkMode ? 'true' : 'false' }),
      });
      await fetch(`${API_URL}/system-config/language`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: language }),
      });
      await fetch(`${API_URL}/system-config/showRoomData`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: showRoomData ? 'true' : 'false' }),
      });
      await fetch(`${API_URL}/system-config/showUserData`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: showUserData ? 'true' : 'false' }),
      });
      await fetch(`${API_URL}/system-config/qrCodeSearchAction`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: qrCodeSearchAction }),
      });
      await fetch(`${API_URL}/system-config/autoOpenQRCodeScanner`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: autoOpenQRCodeScanner ? 'true' : 'false' }),
      });
      await fetch(`${API_URL}/system-config/showConfirmation`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: showConfirmation ? 'true' : 'false' }),
      });
      await fetch(`${API_URL}/system-config/returnToAccessAfterAction`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: returnToAccessAfterAction ? 'true' : 'false' }),
      });
      await fetch(`${API_URL}/system-config/allowMultipleEntries`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: allowMultipleEntries ? 'true' : 'false' }),
      });

      setConfig({
        darkMode,
        language,
        showRoomData,
        showUserData,
        qrCodeSearchAction,
        autoOpenQRCodeScanner,
        showConfirmation,
        returnToAccessAfterAction,
        allowMultipleEntries,
      });
      onClose();
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="sm">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Configurações do Sistema</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {loading ? (
            <Flex justify="center" align="center" minH="100px">
              <Spinner />
            </Flex>
          ) : (
            <VStack spacing={2} align="stretch">
              {/* Modo Escuro */}
              <FormControl mb={0}>
                <Flex justify="space-between" align="center">
                  <Flex align="center" gap={2}>
                    <FormLabel htmlFor="dark-mode" mb="0">
                      Modo Escuro:
                    </FormLabel>
                    <ConfigHelp explanation="Quando ativado, a interface será exibida com cores escuras, facilitando a visualização em ambientes com pouca luz." />
                  </Flex>
                  <Switch
                    id="dark-mode"
                    isChecked={darkMode}
                    onChange={(e) => setDarkMode(e.target.checked)}
                  />
                </Flex>
              </FormControl>
              {/* Idioma */}
              <FormControl mb={0}>
                <Flex direction="column" align="stretch">
                  <Flex align="center" gap={2}>
                    <FormLabel mb="0">Idioma:</FormLabel>
                    <ConfigHelp explanation="Seleciona o idioma da interface do sistema." />
                  </Flex>
                  <Select mt={-4} value={language} onChange={(e) => setLanguage(e.target.value)}>
                    <option value="pt-BR">Português (Brasil)</option>
                    <option value="en-US">English (US)</option>
                    <option value="es-ES">Español</option>
                  </Select>
                </Flex>
              </FormControl>
              {/* Ação ao Buscar Credenciado */}
              <FormControl mb={0}>
                <Flex direction="column" align="stretch">
                  <Flex align="center" gap={2}>
                    <FormLabel mb="0">Ação Buscar Credenciado:</FormLabel>
                    <ConfigHelp explanation="Define a ação a ser realizada automaticamente após a busca por QRCode: entrar/sair, apenas entrar ou exibir dados." />
                  </Flex>
                  <Select mt={-4} value={qrCodeSearchAction} onChange={(e) => setQrCodeSearchAction(e.target.value)}>
                    <option value="enterOrExitRoom">Entrar ou Sair da Sala</option>
                    <option value="onlyEnterRoom">Apenas Entrar na Sala</option>
                    <option value="presentUserData">Exibir Dados do Usuário</option>
                  </Select>
                </Flex>
              </FormControl>
              {/* Mostrar Dados da Sala */}
              <FormControl mb={0}>
                <Flex justify="space-between" align="center">
                  <Flex align="center" gap={2}>
                    <FormLabel htmlFor="show-room-data" mb="0">
                      Dados da Sala:
                    </FormLabel>
                    <ConfigHelp explanation="Se ativado, os detalhes da sala serão exibidos na página." />
                  </Flex>
                  <Switch                
                    id="show-room-data"
                    isChecked={showRoomData}
                    onChange={(e) => setShowRoomData(e.target.checked)}
                  />
                </Flex>
              </FormControl>
              {/* Mostrar Dados do Usuário */}
              <FormControl mb={0}>
                <Flex justify="space-between" align="center">
                  <Flex align="center" gap={2}>
                    <FormLabel htmlFor="show-user-data" mb="0">
                      Dados do Usuário:
                    </FormLabel>
                    <ConfigHelp explanation="Exibe o box com os dados do credenciado." />
                  </Flex>
                  <Switch
                    id="show-user-data"
                    isChecked={showUserData}
                    onChange={(e) => setShowUserData(e.target.checked)}
                  />
                </Flex>
              </FormControl>
              {/* Abrir Leitor de QRCode após Ação */}
              <FormControl mb={0}>
                <Flex justify="space-between" align="center">
                  <Flex align="center" gap={2}>
                    <FormLabel htmlFor="auto-open-scanner" mb="0">
                      Leitor QRcode Automático:
                    </FormLabel>
                    <ConfigHelp explanation="Se ativado, o leitor de QRCode será aberto automaticamente após uma ação." />
                  </Flex>
                  <Switch
                    id="auto-open-scanner"
                    isChecked={autoOpenQRCodeScanner}
                    onChange={(e) => setAutoOpenQRCodeScanner(e.target.checked)}
                  />
                </Flex>
              </FormControl>
              {/* Mostrar Tela de Confirmação */}
              <FormControl mb={0}>
                <Flex justify="space-between" align="center">
                  <Flex align="center" gap={2}>
                    <FormLabel htmlFor="show-confirmation" mb="0">
                      Tela de Confirmação:
                    </FormLabel>
                    <ConfigHelp explanation="Determina se uma tela de confirmação será exibida antes de concluir uma ação." />
                  </Flex>
                  <Switch
                    id="show-confirmation"
                    isChecked={showConfirmation}
                    onChange={(e) => setShowConfirmation(e.target.checked)}
                  />
                </Flex>
              </FormControl>
              {/* Retornar para recepção */}
              <FormControl mb={0}>
                <Flex justify="space-between" align="center">
                  <Flex align="center" gap={2}>
                    <FormLabel htmlFor="return-access" mb="0">
                      Retornar para recepção:
                    </FormLabel>
                    <ConfigHelp explanation="Se ativado, após a ação o sistema redireciona para a página de recepção (/access)." />
                  </Flex>
                  <Switch
                    id="return-access"
                    isChecked={returnToAccessAfterAction}
                    onChange={(e) => setReturnToAccessAfterAction(e.target.checked)}
                  />
                </Flex>
              </FormControl>
              <FormControl mb={0}>
                <Flex justify="space-between" align="center">
                  <Flex align="center" gap={2}>
                    <FormLabel htmlFor="allow-multiple-entries" mb="0">
                      Permitir Múltiplas Entradas:
                    </FormLabel>
                    <ConfigHelp explanation="Se ativado, permitirá dar entrada várias vezes sem exigir saída anterior." />
                  </Flex>
                  <Switch
                    id="allow-multiple-entries"
                    isChecked={allowMultipleEntries}
                    onChange={(e) => setAllowMultipleEntries(e.target.checked)}
                  />
                </Flex>
              </FormControl>
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" colorScheme="blue" mr={3} onClick={onClose} isDisabled={saving}>
            Cancelar
          </Button>
          <Button variant="ghost" onClick={handleSave} isLoading={saving} isDisabled={loading}>
            Salvar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ConfigModal;
